import axios from 'axios';
import config from '@config';
import { authService } from '@features/auth/services/authService';

// The rides (hoprides) API is hit directly in deployed environments; in local
// dev we proxy through Netlify functions to avoid CORS.
function resolveRidesEndpoint(path: string): string {
  const isDevelopment =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isDevelopment) {
    return `${window.location.origin}/.netlify/functions/${path}`;
  }
  return `${config.hopridesBaseUrl}/v1/${path}`;
}

/**
 * Cancel the provider (rideshare) ride behind a guestride.
 *
 * Cancelling a guestride by only flipping `guestrides.status` doesn't stick:
 * the live ridehail re-syncs the real provider status back over it. We must
 * cancel the provider ride too, which moves the ridehail to `cancelled` so the
 * sync agrees.
 *
 * The rides API keys off the internal `ridehails.id` (param `ridehailId`),
 * NOT the provider's `external_id` — it resolves the provider ride server-side.
 * (Matches admin-portal's working `app-cancel-ride` call.)
 */
export async function cancelProviderRide(ridehailId: number): Promise<void> {
  const token = await authService.getAccessTokenWithAutoRefresh();
  await axios({
    method: 'POST',
    url: resolveRidesEndpoint('app-cancel-ride'),
    data: { ridehailId },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Manager approval actions. Always the rides API itself: its CORS allows every *.hopdrive.io
 * portal host, including portal.local, so these need no local proxy.
 */
async function postApprovalAction<T>(path: string, data: Record<string, unknown>): Promise<T> {
  const token = await authService.getAccessTokenWithAutoRefresh();
  const response = await axios({
    method: 'POST',
    url: `${config.hopridesBaseUrl}/v1/${path}`,
    data,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  return response.data as T;
}

export const approveGuestRide = (guestrideId: number) =>
  postApprovalAction<{ success: boolean }>('app-approve-guestride', { guestrideId });

export const denyGuestRide = (guestrideId: number, reason?: string) =>
  postApprovalAction<{ success: boolean }>('app-deny-guestride', { guestrideId, reason: reason || null });

export const resendGuestRideApproval = (guestrideId: number) =>
  postApprovalAction<{ success: boolean; notified?: Record<string, string> }>('app-resend-guestride-approval', {
    guestrideId,
  });

/** The rides API's refusal message, when it sent one. */
export const ridesApiError = (err: unknown): { status?: number; message?: string; retryAfterSeconds?: number } => {
  const response = (err as { response?: { status?: number; data?: { error?: string; reason?: string; retry_after_seconds?: number } } })
    ?.response;
  return {
    status: response?.status,
    message: response?.data?.error ?? response?.data?.reason,
    retryAfterSeconds: response?.data?.retry_after_seconds,
  };
};
